<template>
  <!-- ✅ POPUP DE NOTIFICAÇÕES -->
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">

      <!-- Header -->
      <div class="bg-blue-600 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
        <h3 class="text-xl font-bold">
          <i class="fas fa-bell mr-2"></i>
          Histórico da OS #{{ ordem_servico_id }}
        </h3>
        <button @click="fechar" class="hover:bg-blue-700 rounded-full p-2 transition">
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-6">

        <!-- Loading -->
        <div v-if="loading" class="text-center py-12">
          <i class="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4"></i>
          <p class="text-gray-600 dark:text-gray-300">Carregando histórico...</p>
        </div>

        <!-- Sem dados -->
        <div v-else-if="!historico || historico.length === 0" class="text-center py-12">
          <i class="fas fa-inbox text-6xl text-gray-400 mb-4"></i>
          <p class="text-gray-600 dark:text-gray-300 text-lg">Nenhum histórico registado para esta OS</p>
        </div>

        <!-- Conteúdo -->
        <div v-else class="space-y-6">

          <!-- ✅ DECISÃO MAIS RECENTE (confirmação/adiamento/cancelamento) -->
          <div v-if="decisaoFinal"
            class="border-2 rounded-lg p-5 bg-gradient-to-r"
            :class="decisaoFinalClasses.container">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 rounded-full flex items-center justify-center" :class="decisaoFinalClasses.iconBg">
                  <i class="fas text-white text-xl" :class="decisaoFinalClasses.icon"></i>
                </div>
              </div>

              <div class="flex-1">
                <h4 class="text-lg font-bold mb-2" :class="decisaoFinalClasses.title">
                  {{ getEventoLabel(decisaoFinal) }}
                </h4>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span class="font-semibold text-gray-700 dark:text-gray-300">
                      {{ decisaoFinal.ator_tipo === 'cliente' ? 'Respondido por:' : 'Decidido por:' }}
                    </span>
                    <span class="ml-2 text-gray-900 dark:text-white normal-case">{{ decisaoFinal.ator_nome || 'N/A' }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700 dark:text-gray-300">Data:</span>
                    <span class="ml-2 text-gray-900 dark:text-white">{{ formatDateTime(decisaoFinal.criado_em) }}</span>
                  </div>
                  <div v-if="decisaoFinal.email_recipient && decisaoFinal.email_recipient !== decisaoFinal.ator_nome" class="md:col-span-2">
                    <span class="font-semibold text-gray-700 dark:text-gray-300">Email enviado para:</span>
                    <span class="ml-2 text-gray-900 dark:text-white normal-case">{{ decisaoFinal.email_recipient }}</span>
                  </div>
                </div>

                <div v-if="parseDetalhes(decisaoFinal.detalhes).length" class="mt-3 pt-3 border-t border-gray-300/30">
                  <div v-for="parte in parseDetalhes(decisaoFinal.detalhes)" :key="parte.label" class="text-sm mb-1">
                    <span class="font-semibold text-gray-700 dark:text-gray-300">{{ parte.label }}:</span>
                    <span class="ml-2 text-gray-900 dark:text-white">{{ parte.value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ✅ HISTÓRICO COMPLETO (alterações da OS + eventos de email) -->
          <div>
            <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <i class="fas fa-history mr-2 text-blue-500"></i>
              Histórico completo ({{ historico.length }})
            </h4>

            <div class="space-y-3">
              <div v-for="item in historico" :key="item.id"
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                <div class="flex items-start gap-3">
                  <!-- Ícone da categoria -->
                  <div class="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                    :class="item.categoria === 'email' ? 'bg-sky-100 dark:bg-sky-900' : 'bg-purple-100 dark:bg-purple-900'">
                    <i class="fas text-sm"
                      :class="item.categoria === 'email' ? 'fa-envelope text-sky-600 dark:text-sky-300' : 'fa-pen text-purple-600 dark:text-purple-300'"></i>
                  </div>

                  <div class="flex-1 min-w-0">
                    <div class="flex items-center flex-wrap gap-2 mb-1">
                      <span class="px-2 py-0.5 rounded text-[11px] font-semibold" :class="getEventoClassBadge(item.evento)">
                        {{ getEventoLabel(item) }}
                      </span>
                      <span v-if="item.status" class="text-xs text-gray-500 dark:text-gray-400">
                        Estado: <strong>{{ item.status }}</strong>
                      </span>
                      <span class="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                        {{ formatDateTime(item.criado_em) }}
                      </span>
                    </div>

                    <p class="text-sm text-gray-700 dark:text-gray-300">
                      <i class="fas mr-1"
                        :class="item.ator_tipo === 'cliente' ? 'fa-user text-amber-500' : (item.ator_tipo === 'interno' ? 'fa-user-tie text-blue-500' : 'fa-gear text-gray-400')"></i>
                      {{ item.ator_nome || (item.ator_tipo === 'sistema' ? 'Sistema' : 'N/A') }}
                    </p>

                    <p v-if="item.email_recipient && item.email_recipient !== item.ator_nome" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <i class="fas fa-envelope mr-1 text-sky-500"></i>
                      {{ item.email_recipient }}
                    </p>

                    <div v-if="parseDetalhes(item.detalhes).length" class="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                      <div v-for="parte in parseDetalhes(item.detalhes)" :key="parte.label">
                        <span class="font-medium">{{ parte.label }}:</span> {{ parte.value }}
                      </div>
                    </div>
                  </div>

                  <!-- Reenviar: só faz sentido para o próprio evento de envio
                       de email, e só enquanto o agendamento ainda não tiver
                       sido confirmado (o backend também bloqueia isto). -->
                  <button
                    v-if="podeReenviar(item)"
                    @click="reenviarNotificacao(item)"
                    :disabled="reenviandoId === item.id"
                    class="flex-shrink-0 p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Reenviar email"
                  >
                    <i class="fas" :class="reenviandoId === item.id ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Footer (ações) -->
      <div class="bg-gray-100 dark:bg-gray-900 px-6 py-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          <i class="fas fa-info-circle mr-1"></i>
          {{ footerStatusMessage }}
        </p>
        <div class="flex gap-3">
          <button v-if="!jaCancelada" @click="cancelarAgendamento(ordem_servico_id)"
            :disabled="cancel_disabled"
            class="px-4 py-2 bg-red-600 text-white rounded-lg transition"
            :class="cancel_disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'"
            :title="cancel_disabled ? 'Não é possível cancelar um agendamento depois de iniciado' : ''">
            <i class="fas fa-check mr-2"></i>
            Cancel Agendamento
          </button>
          <button @click="fechar"
            class="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 transition">
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmação de reenvio (mantido dentro da raiz única do template —
         é position:fixed, por isso fica por cima na mesma). -->
    <SimpleConfirmComponent
      v-if="itemParaReenviar"
      :message="`Deseja reenviar o email para ${itemParaReenviar.email_recipient}?`"
      @confirm="confirmarReenvio"
      @cancel="itemParaReenviar = null"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import { toast } from 'vue3-toastify';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import SimpleConfirmComponent from '@/components/simpleConfirmComponent.vue';
import {
  EVENTOS_DECISIVOS,
  getEventoLabel,
  getEventoClass as getEventoClassBadge,
  formatDateTime,
  parseDetalhes,
} from '@/utils/ordemServicoHistoricoHelpers';

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  ordem_servico_id: {
    type: [String, Number],
    default: null
  },
  cancel_disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'refresh']);

const baseURl = process.env.VUE_APP_URL_BASE;

const historico = ref([]);
const loading = ref(false);

// A decisão mais recente entre os eventos que fecham o ciclo do agendamento
// (confirmação, adiamento ou cancelamento — do cliente ou de um admin).
// Olha só para o evento de email mais recente (não para "alguma vez existiu
// uma decisão nesta OS") — senão uma confirmação/cancelamento antigos ficam
// presos como "finais" mesmo depois de um novo email ter sido reenviado à
// espera de nova resposta. `historico` já vem ordenado do backend por
// -criado_em, por isso o primeiro evento de email da lista é o mais recente.
const decisaoFinal = computed(() => {
  const eventoEmailMaisRecente = historico.value.find((item) => item.categoria === 'email');
  if (!eventoEmailMaisRecente) return null;
  return EVENTOS_DECISIVOS.includes(eventoEmailMaisRecente.evento) ? eventoEmailMaisRecente : null;
});

// Um agendamento confirmado ainda PODE ser cancelado (é literalmente para
// isso que serve o botão) — só um que já está cancelado é que não faz
// sentido voltar a cancelar. `cancel_disabled` (vindo do pai) trata a outra
// razão real para desativar o botão: o serviço já arrancou.
const jaCancelada = computed(() => decisaoFinal.value?.evento === 'cancelado_admin');

const footerStatusMessage = computed(() => {
  if (jaCancelada.value) return 'Agendamento cancelado';
  if (!decisaoFinal.value) return 'Aguarda resposta do cliente';
  return getEventoLabel(decisaoFinal.value);
});

const decisaoFinalClasses = computed(() => {
  const evento = (decisaoFinal.value?.evento || '').toLowerCase();
  if (['confirmado', 'confirmado_admin', 'aprovado_admin'].includes(evento)) {
    return {
      container: 'from-green-50 to-green-100 border-green-500 dark:from-green-900 dark:to-green-800',
      iconBg: 'bg-green-500',
      icon: 'fa-check',
      title: 'text-green-800 dark:text-green-200',
    };
  }
  if (['cancelado_admin', 'rejeitado_admin'].includes(evento)) {
    return {
      container: 'from-red-50 to-red-100 border-red-500 dark:from-red-900 dark:to-red-800',
      iconBg: 'bg-red-500',
      icon: 'fa-ban',
      title: 'text-red-800 dark:text-red-200',
    };
  }
  return {
    container: 'from-yellow-50 to-yellow-100 border-yellow-500 dark:from-yellow-900 dark:to-yellow-800',
    iconBg: 'bg-yellow-500',
    icon: 'fa-clock',
    title: 'text-yellow-800 dark:text-yellow-200',
  };
});

// Mesma condição que o backend aplica em SendOrdemServicoEmailView (bloqueia
// reenvio só quando a OS já está confirmada) — replicada aqui para não
// mostrar um botão que o pedido ia rejeitar de qualquer forma.
function podeReenviar(item) {
  if (item.categoria !== 'email') return false;
  if (!['enviado', 'reenviado'].includes(item.evento)) return false;
  if (!item.email_recipient) return false;
  const evento = (decisaoFinal.value?.evento || '').toLowerCase();
  return !['confirmado', 'confirmado_admin', 'aprovado_admin'].includes(evento);
}

const reenviandoId = ref(null);
// Item aguardando confirmação no SimpleConfirmComponent (em vez do confirm()
// nativo do browser) — não nulo == modal de confirmação aberto.
const itemParaReenviar = ref(null);

function reenviarNotificacao(item) {
  if (reenviandoId.value) return;
  itemParaReenviar.value = item;
}

async function confirmarReenvio() {
  const item = itemParaReenviar.value;
  itemParaReenviar.value = null;
  if (!item) return;

  reenviandoId.value = item.id;
  try {
    const res = await axios.post(`${baseURl}/api/v1/ordemservico-email/send/`, {
      recipients: [{ email: item.email_recipient, ordemservico_ids: [Number(props.ordem_servico_id)] }],
      cc: [],
      notify_gestores: [],
      subject: 'Intervenções Agendadas',
      message: '',
      observacoes_ordem: {},
    });

    if (res.data?.status === 200 && res.data?.data?.successful > 0) {
      toast.success('Email reenviado com sucesso!', {
        theme: 'auto', position: 'top-center', autoClose: 2000,
      });
      await buscarHistorico();
      emit('refresh');
    } else {
      toast.error(res.data?.message || 'Erro ao reenviar email', {
        theme: 'auto', position: 'top-center', autoClose: 3000,
      });
    }
  } catch (error) {
    console.error('Erro ao reenviar email:', error);
    toast.error(error.response?.data?.message || 'Erro ao reenviar email', {
      theme: 'auto', position: 'top-center', autoClose: 3000,
    });
  } finally {
    reenviandoId.value = null;
  }
}

async function cancelarAgendamento(ordem_servico_id) {
  // Serviços já iniciados (fechado/interrompido) não podem ser cancelados
  if (props.cancel_disabled) {
    return;
  }

  const { value: observacoes, isConfirmed } = await Swal.fire({
    title: 'Cancelar Agendamento',
    html: `
      <div class="text-left">
        <textarea
          id="swal-input-observacoes"
          class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          placeholder="Descreva o motivo do cancelamento..."
          rows="4"
        ></textarea>
      </div>
    `,
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText: '<i class="fas fa-ban mr-2"></i>Sim, cancelar!',
    reverseButtons: true,
    focusConfirm: false,
    preConfirm: () => {
      const textarea = document.getElementById('swal-input-observacoes');
      const value = textarea.value.trim();

      if (!value) {
        Swal.showValidationMessage('Por favor, informe o motivo do cancelamento');
        return false;
      }

      return value;
    },
    didOpen: () => {
      document.getElementById('swal-input-observacoes').focus();
    }
  });

  if (!isConfirmed || !observacoes) {
    return;
  }

  Swal.fire({
    title: 'Cancelando...',
    html: 'Aguarde enquanto processamos o cancelamento.',
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });

  try {
    await axios.post(`${baseURl}/api/v1/ordemservico/cancelar_agendamento/${ordem_servico_id}/`,
      { motivo_cancelamento: observacoes }
    );

    Swal.fire({
      title: 'Cancelado!',
      html: `O agendamento <strong>#${ordem_servico_id}</strong> foi cancelado com sucesso.`,
      icon: 'success',
      confirmButtonColor: '#10b981',
      confirmButtonText: 'Ok'
    });

    buscarHistorico();
    emit('refresh');

  } catch (error) {
    console.error('Erro ao cancelar agendamento:', error);

    Swal.fire({
      title: 'Erro!',
      text: error.response?.data?.message || 'Não foi possível cancelar o agendamento. Tente novamente.',
      icon: 'error',
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Entendi'
    });
  }
}

watch(() => props.show, (newVal) => {
  if (newVal && props.ordem_servico_id) {
    buscarHistorico();
  }
});

const buscarHistorico = async () => {
  loading.value = true;
  try {
    // Sem corte de datas: para uma única OS o backend devolve tudo (ver
    // OrdemServicoHistoricoTimelineView), evitando esconder eventos com
    // mais de 30 dias.
    const response = await axios.get(
      `${baseURl}/api/v1/ordemservico/historico/pagination/`,
      { params: { ordemservico_id: props.ordem_servico_id, page_size: 200 } }
    );
    historico.value = response.data?.results || [];
  } catch (error) {
    console.error('Erro ao buscar histórico da OS:', error);
    toast('Erro ao carregar histórico!', {
      theme: 'auto',
      type: 'error',
      position: 'top-center',
      autoClose: 2000,
    });
    historico.value = [];
  } finally {
    loading.value = false;
  }
};

const fechar = () => {
  emit('close');
};
</script>
